/**
 * The planet-scale (classic) rocks merged per planet (the perf backlog's rock merge, held since the render pass
 * because "rocks have health and fall with terrain"; lane F).
 *
 * The rule: **the merged meshes are a cache of the rocks' state, rebuilt per planet when it changes.** Each rock
 * keeps its own model as the render pass built it — its seeded shape, baked to a mesh or two, seated on the
 * ground (`createRockMesh`) — as a source that is never drawn; what is drawn is one mesh per baked material per
 * planet, merged from the sources of that planet's standing rocks. When the ground under a planet's rocks moves
 * (a crater: the sources are re-seated, as the old meshes were) or one of them goes (`alive` false: a felled set
 * today, a rock with health once 63.4 gives it one), only that planet's merge is rebuilt — a handful of rocks,
 * well under a millisecond. Nothing is lost by merging: no rock is picked, raycast or animated on its own.
 *
 * The full map's 44 rocks were 54 meshes; merged, one per material a planet: 19. The person-scale rocks of a battle's stands
 * are not here: they are instanced in the stands (treeStands.ts), which also move and hide them one at a time.
 *
 * Presentation only: reads the state, writes none.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { PlanetState, RockState } from '../types';
import { getRockWorldPosition } from '../core/gameState';
import { createRockMesh, reseatPropMesh, disposeRockMesh, type RockMesh, type PropScaleOpts } from './renderer';

export class PlanetRockMerge {
  /** The rocks' own meshes: the merge's sources, never in the drawn scene. */
  private readonly holder = new THREE.Scene();
  private readonly sources: Array<{ rock: RockState; mesh: RockMesh; alive: boolean }> = [];
  /** planet index → its drawn group (one mesh per baked material) */
  private readonly drawn = new Map<number, THREE.Group>();
  private readonly deadPlanets = new Set<number>();
  private lastRocks: readonly RockState[] | null = null;

  /** Every rock in `rocks` (the caller picks which: the planet-scale ones), placed as `createRockMesh` places it. */
  constructor(private readonly scene: THREE.Scene, rocks: readonly RockState[], planets: PlanetState[], optsFor: (planetIndex: number) => PropScaleOpts) {
    for (const rock of rocks) {
      const planet = planets[rock.planetIndex];
      if (!planet) continue;
      const p = getRockWorldPosition(rock, planets);
      const n = new THREE.Vector3(rock.surfaceDir.x, rock.surfaceDir.y, rock.surfaceDir.z).normalize();
      const mesh = createRockMesh(rock, new THREE.Vector3(p.x, p.y, p.z), n, this.holder, planet.radius, optsFor(rock.planetIndex));
      this.sources.push({ rock, mesh, alive: rock.alive });
    }
    for (const pi of new Set(this.sources.map(s => s.rock.planetIndex))) this.rebuild(pi, planets);
  }

  /** Drawn meshes in the scene (the merge's draws). */
  get meshCount(): number { let n = 0; for (const g of this.drawn.values()) n += g.children.length; return n; }

  /** The ground moved on `planetIndex` (a crater): its rocks are re-seated and its merge rebuilt. */
  groundChanged(planets: PlanetState[], planetIndex: number): void {
    let any = false;
    for (const s of this.sources) if (s.rock.planetIndex === planetIndex && s.mesh.seat) { reseatPropMesh(s.mesh, planets); any = true; }
    if (any) this.rebuild(planetIndex, planets);
  }

  /** The state's rocks changed (a new array): a rock that went leaves its planet's merge. */
  follow(rocks: readonly RockState[], planets: PlanetState[]): void {
    if (rocks === this.lastRocks) return;
    this.lastRocks = rocks;
    const alive = new Map<number, boolean>();
    for (const r of rocks) alive.set(r.id, r.alive);
    const changed = new Set<number>();
    for (const s of this.sources) {
      const a = alive.get(s.rock.id) ?? s.alive;
      if (a !== s.alive) { s.alive = a; changed.add(s.rock.planetIndex); }
    }
    for (const pi of changed) this.rebuild(pi, planets);
  }

  /** Everything on a destroyed planet leaves the scene. */
  hidePlanet(planetIndex: number): void {
    this.deadPlanets.add(planetIndex);
    const g = this.drawn.get(planetIndex);
    if (g) g.visible = false;
  }

  dispose(): void {
    for (const g of this.drawn.values()) this.drop(g);
    this.drawn.clear();
    for (const s of this.sources) disposeRockMesh(this.holder, s.mesh);
    this.sources.length = 0;
  }

  /**
   * Merge a planet's standing rocks: every source mesh in its world place (taken relative to the planet's centre,
   * so the vertices keep their precision), grouped by material — the bake's shared buckets, so a planet's rocks
   * come to one or two meshes.
   */
  private rebuild(planetIndex: number, planets: PlanetState[]): void {
    const old = this.drawn.get(planetIndex);
    if (old) this.drop(old);
    this.drawn.delete(planetIndex);
    const planet = planets[planetIndex];
    if (!planet) return;
    const origin = new THREE.Matrix4().makeTranslation(-planet.position.x, -planet.position.y, -planet.position.z);
    const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
    for (const s of this.sources) {
      if (s.rock.planetIndex !== planetIndex || !s.alive) continue;
      s.mesh.group.updateMatrixWorld(true);
      s.mesh.group.traverse(o => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || !m.visible || Array.isArray(m.material)) return;
        for (let p: THREE.Object3D | null = m; p && p !== s.mesh.group.parent; p = p.parent) if (!p.visible) return;
        const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
        g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(origin, m.matrixWorld));
        let list = byMaterial.get(m.material);
        if (!list) byMaterial.set(m.material, list = []);
        list.push(g);
      });
    }
    if (byMaterial.size === 0) return;
    const group = new THREE.Group();
    group.position.set(planet.position.x, planet.position.y, planet.position.z);
    group.matrixAutoUpdate = false;
    group.updateMatrix();
    group.visible = !this.deadPlanets.has(planetIndex);
    for (const [material, geos] of byMaterial) {
      const merged = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
      if (geos.length > 1) for (const g of geos) g.dispose();
      if (!merged) continue;
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, material);
      mesh.matrixAutoUpdate = false;
      mesh.userData.baked = true;
      group.add(mesh);
    }
    this.scene.add(group);
    this.drawn.set(planetIndex, group);
  }

  /** Take a planet's merged group out of the scene; its geometries were made here, its materials are the bake's (shared). */
  private drop(g: THREE.Group): void {
    this.scene.remove(g);
    g.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
  }
}
